
import { Button } from "@/components/ui/button";
import { TrendingUp, Heart, Sparkles } from "lucide-react";
import { useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface GroupFiltersProps {
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  showTrending: boolean;
  onTrendingChange: (trending: boolean) => void;
  showMyGroups?: boolean;
  onMyGroupsChange?: (showMyGroups: boolean) => void;
}

export const GroupFilters = ({ 
  selectedCategory, 
  onCategoryChange, 
  showTrending, 
  onTrendingChange,
  showMyGroups = false,
  onMyGroupsChange
}: GroupFiltersProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
      <div className="flex flex-col sm:flex-row gap-3 w-full">
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 sm:mb-0 sm:self-center shrink-0">
          Quick Filters:
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <Button
            variant={showTrending ? "default" : "outline"}
            className={`transition-all duration-200 px-3 sm:px-4 py-2 rounded-full font-semibold shadow-md text-sm whitespace-nowrap ${
              showTrending 
                ? 'bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 text-white' 
                : 'bg-white/80 text-orange-600 border-orange-200 hover:bg-orange-50 hover:border-orange-300'
            }`}
            onClick={() => onTrendingChange(!showTrending)}
          >
            <Sparkles className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">Popular Now</span>
            <span className="xs:hidden">Popular</span>
          </Button>
          
          <Button
            variant={showMyGroups ? "default" : "outline"}
            className={`transition-all duration-200 px-3 sm:px-4 py-2 rounded-full font-semibold shadow-md text-sm whitespace-nowrap ${
              showMyGroups 
                ? 'bg-gradient-to-r from-purple-400 to-blue-400 hover:from-purple-500 hover:to-blue-500 text-white' 
                : 'bg-white/80 text-purple-600 border-purple-200 hover:bg-purple-50 hover:border-purple-300'
            }`}
            onClick={() => onMyGroupsChange?.(!showMyGroups)}
          >
            <Heart className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">My Communities</span>
            <span className="xs:hidden">My Groups</span>
          </Button>
        </div>
      </div>

      <div className="w-full lg:w-auto shrink-0">
        <Button
          variant="outline"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full lg:w-auto bg-white/80 border-purple-200 hover:bg-purple-50 hover:border-purple-300 rounded-xl"
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          Browse Categories {isOpen ? '↑' : '↓'}
        </Button>
      </div>
      
      {isOpen && (
        <div className="w-full mt-4 p-4 bg-white/60 dark:bg-gray-800/60 rounded-2xl border-2 border-purple-100 dark:border-purple-800">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
            🎯 Find Your Perfect Community
          </h3>
          <Tabs value={selectedCategory} className="w-full" onValueChange={onCategoryChange}>
            <TabsList className="w-full grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 bg-transparent">
              <TabsTrigger value="all" className="rounded-xl bg-white/80 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-400 data-[state=active]:to-pink-400 data-[state=active]:text-white">💫 All</TabsTrigger>
              <TabsTrigger value="wellness" className="rounded-xl bg-white/80 data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-400 data-[state=active]:to-blue-400 data-[state=active]:text-white">🌱 Wellness</TabsTrigger>
              <TabsTrigger value="mental_health" className="rounded-xl bg-white/80 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-400 data-[state=active]:to-purple-400 data-[state=active]:text-white">🧠 Mental Health</TabsTrigger>
              <TabsTrigger value="support" className="rounded-xl bg-white/80 data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-400 data-[state=active]:to-red-400 data-[state=active]:text-white">🤝 Support</TabsTrigger>
              <TabsTrigger value="lifestyle" className="rounded-xl bg-white/80 data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-400 data-[state=active]:to-orange-400 data-[state=active]:text-white">✨ Lifestyle</TabsTrigger>
              <TabsTrigger value="relationships" className="rounded-xl bg-white/80 data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-400 data-[state=active]:to-pink-400 data-[state=active]:text-white">💕 Relationships</TabsTrigger>
              <TabsTrigger value="personal_growth" className="rounded-xl bg-white/80 data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-400 data-[state=active]:to-blue-400 data-[state=active]:text-white">🚀 Growth</TabsTrigger>
              <TabsTrigger value="other" className="rounded-xl bg-white/80 data-[state=active]:bg-gradient-to-r data-[state=active]:from-gray-400 data-[state=active]:to-gray-600 data-[state=active]:text-white">🌈 Other</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}
    </div>
  );
};
